/**
 * Simple Cache Manager Module - IPHUB Project
 * Main cache manager class with ESM structure
 */

import { DEFAULT_CONFIG, ERROR_MESSAGES, DEBUG_MESSAGES } from "./config.js";

import {
  checkStorageAvailability,
  calculateCurrentSize,
  generateKey,
  createCacheItem,
  isExpired,
  calculateItemSize,
  formatDebugMessage,
  logDebug,
  logWarning,
  logError,
  collectCacheKeys,
  removeCacheKeys,
  parseCacheItem,
  validateCacheItem,
} from "./utils.js";

import {
  getStats,
  incrementHits,
  incrementMisses,
  incrementSets,
  incrementDeletes,
  incrementCleanups,
  incrementErrors,
  resetStats,
  calculateHitRate,
  getDetailedStats,
} from "./statistics.js";

/**
 * Simple Cache Manager Class
 */
export class SimpleCacheManager {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.memoryCache = new Map();
    this.isAvailable = checkStorageAvailability();
    this.currentSize = 0;

    if (this.isAvailable) {
      this._initializeCache();
    }
  }

  /**
   * Initialize cache
   */
  _initializeCache() {
    try {
      this.currentSize = calculateCurrentSize(this.config.prefix);
      this._cleanupExpiredItems();

      logDebug(
        formatDebugMessage(DEBUG_MESSAGES.INITIALIZED, {
          size: this.currentSize,
        }),
        this.config.debug,
      );
    } catch (error) {
      logError("SimpleCache: Error during initialization", error);
      incrementErrors();
    }
  }

  /**
   * Get item from cache
   * @param {string} key - Cache key
   * @param {string} prefix - Optional prefix for namespace
   * @returns {any|null} Cached data or null
   */
  get(key, prefix = "") {
    if (!this.isAvailable) return null;

    try {
      const cacheKey = generateKey(key, prefix, this.config.prefix, this.config.version);

      // Check memory cache first
      if (this.memoryCache.has(cacheKey)) {
        const memoryItem = this.memoryCache.get(cacheKey);
        if (!isExpired(memoryItem)) {
          incrementHits();
          logDebug(
            formatDebugMessage(DEBUG_MESSAGES.MEMORY_HIT, { key: cacheKey }),
            this.config.debug,
          );
          return memoryItem.data;
        } else {
          this.memoryCache.delete(cacheKey);
        }
      }

      // Check localStorage
      const serializedItem = localStorage.getItem(cacheKey);
      if (!serializedItem) {
        incrementMisses();
        return null;
      }

      const cacheItem = parseCacheItem(serializedItem);
      if (!cacheItem) {
        this.delete(key, prefix);
        incrementMisses();
        return null;
      }

      // Check expiration
      if (isExpired(cacheItem)) {
        this.delete(key, prefix);
        incrementMisses();
        return null;
      }

      // Check version
      if (cacheItem.version !== this.config.version) {
        this.delete(key, prefix);
        incrementMisses();
        return null;
      }

      // Update memory cache
      this.memoryCache.set(cacheKey, cacheItem);
      incrementHits();

      logDebug(
        formatDebugMessage(DEBUG_MESSAGES.STORAGE_HIT, { key: cacheKey }),
        this.config.debug,
      );

      return cacheItem.data;
    } catch (error) {
      logError(`SimpleCache: Error getting ${key}`, error);
      this.delete(key, prefix); // Remove corrupted data
      incrementErrors();
      return null;
    }
  }

  /**
   * Set item in cache
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number|null} ttl - Time to live in milliseconds (null = persistent)
   * @param {string} prefix - Optional prefix for namespace
   * @returns {boolean} Success status
   */
  set(key, data, ttl = null, prefix = "") {
    if (!this.isAvailable) return false;

    try {
      const cacheKey = generateKey(key, prefix, this.config.prefix, this.config.version);
      const cacheItem = createCacheItem(data, ttl, this.config.version);
      const serializedItem = JSON.stringify(cacheItem);
      const itemSize = calculateItemSize(cacheKey, serializedItem);

      // Check item size limit
      if (itemSize > this.config.storage.maxItemSize) {
        logWarning(
          `${ERROR_MESSAGES.ITEM_TOO_LARGE} for ${cacheKey} (${(itemSize / 1024 / 1024).toFixed(2)}MB)`,
        );
        incrementErrors();
        return false;
      }

      // Check storage space
      if (this.currentSize + itemSize > this.config.storage.maxSize) {
        if (!this._makeSpace(itemSize)) {
          logWarning(`${ERROR_MESSAGES.INSUFFICIENT_SPACE} for ${cacheKey}`);
          incrementErrors();
          return false;
        }
      }

      // Store in memory cache
      this.memoryCache.set(cacheKey, cacheItem);

      // Store in localStorage
      localStorage.setItem(cacheKey, serializedItem);
      this.currentSize += itemSize;
      incrementSets();

      logDebug(
        formatDebugMessage(DEBUG_MESSAGES.CACHED, {
          key: cacheKey,
          size: (itemSize / 1024).toFixed(1),
        }),
        this.config.debug,
      );

      return true;
    } catch (error) {
      logError(`SimpleCache: Error setting ${key}`, error);
      incrementErrors();
      return false;
    }
  }

  /**
   * Delete item from cache
   * @param {string} key - Cache key
   * @param {string} prefix - Optional prefix for namespace
   * @returns {boolean} Success status
   */
  delete(key, prefix = "") {
    if (!this.isAvailable) return false;

    try {
      const cacheKey = generateKey(key, prefix, this.config.prefix, this.config.version);

      // Remove from memory cache
      this.memoryCache.delete(cacheKey);

      // Remove from localStorage
      const serializedItem = localStorage.getItem(cacheKey);
      if (serializedItem) {
        const itemSize = calculateItemSize(cacheKey, serializedItem);
        localStorage.removeItem(cacheKey);
        this.currentSize = Math.max(0, this.currentSize - itemSize);
        incrementDeletes();

        logDebug(formatDebugMessage(DEBUG_MESSAGES.DELETED, { key: cacheKey }), this.config.debug);

        return true;
      }

      return false;
    } catch (error) {
      logError(`SimpleCache: Error deleting ${key}`, error);
      incrementErrors();
      return false;
    }
  }

  /**
   * Clear cache with optional prefix
   * @param {string} prefix - Optional prefix to clear only specific namespace
   * @returns {boolean} Success status
   */
  clear(prefix = "") {
    if (!this.isAvailable) return false;

    try {
      const fullPrefix = prefix ? `${this.config.prefix}${prefix}_` : this.config.prefix;
      const keysToRemove = collectCacheKeys(fullPrefix);

      // Remove from memory cache
      for (const key of this.memoryCache.keys()) {
        if (key.startsWith(fullPrefix)) {
          this.memoryCache.delete(key);
        }
      }

      // Remove from localStorage
      removeCacheKeys(keysToRemove);

      this.currentSize = calculateCurrentSize(this.config.prefix);
      incrementCleanups();

      logDebug(
        formatDebugMessage(DEBUG_MESSAGES.CLEARED, {
          count: keysToRemove.length,
          prefix: prefix ? ` with prefix: ${prefix}` : "",
        }),
        this.config.debug,
      );

      return true;
    } catch (error) {
      logError("SimpleCache: Error clearing cache", error);
      incrementErrors();
      return false;
    }
  }

  /**
   * Check if key exists
   * @param {string} key - Cache key
   * @param {string} prefix - Optional prefix for namespace
   * @returns {boolean} Whether key exists and is valid
   */
  has(key, prefix = "") {
    if (!this.isAvailable) return false;

    try {
      const cacheKey = generateKey(key, prefix, this.config.prefix, this.config.version);

      // Check memory cache
      if (this.memoryCache.has(cacheKey)) {
        const item = this.memoryCache.get(cacheKey);
        return !isExpired(item);
      }

      // Check localStorage
      const serializedItem = localStorage.getItem(cacheKey);
      if (!serializedItem) return false;

      const item = parseCacheItem(serializedItem);
      return item && !isExpired(item) && item.version === this.config.version;
    } catch (error) {
      return false;
    }
  }

  /**
   * Make space for new item
   */
  _makeSpace(requiredSize) {
    try {
      // Clean up expired items first
      this._cleanupExpiredItems();

      // If still not enough space, remove oldest items
      if (this.currentSize + requiredSize > this.config.storage.maxSize) {
        return this._removeOldestItems(requiredSize);
      }

      return true;
    } catch (error) {
      logError("SimpleCache: Error making space", error);
      return false;
    }
  }

  /**
   * Clean up expired items
   */
  _cleanupExpiredItems() {
    if (!this.isAvailable) return 0;

    let cleanedCount = 0;
    const prefix = this.config.prefix;
    const keysToRemove = [];

    try {
      // Collect expired keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          const serializedItem = localStorage.getItem(key);
          if (serializedItem) {
            const cacheItem = parseCacheItem(serializedItem);
            if (!cacheItem || isExpired(cacheItem) || cacheItem.version !== this.config.version) {
              keysToRemove.push(key);
            }
          }
        }
      }

      // Remove expired keys
      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
        this.memoryCache.delete(key);
        cleanedCount++;
      });

      if (cleanedCount > 0) {
        this.currentSize = calculateCurrentSize(this.config.prefix);
        incrementCleanups();

        logDebug(
          formatDebugMessage(DEBUG_MESSAGES.CLEANED_UP, {
            count: cleanedCount,
          }),
          this.config.debug,
        );
      }

      return cleanedCount;
    } catch (error) {
      logError("SimpleCache: Error during cleanup", error);
      return 0;
    }
  }

  /**
   * Remove oldest items to make space
   */
  _removeOldestItems(requiredSize) {
    if (!this.isAvailable) return false;

    try {
      const prefix = this.config.prefix;
      const items = [];

      // Collect all cache items with timestamps
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          const serializedItem = localStorage.getItem(key);
          if (serializedItem) {
            const cacheItem = parseCacheItem(serializedItem);
            if (cacheItem) {
              items.push({
                key: key,
                timestamp: cacheItem.timestamp || 0,
                size: calculateItemSize(key, serializedItem),
              });
            } else {
              // Corrupted item, add to removal list
              items.push({ key: key, timestamp: 0, size: 0 });
            }
          }
        }
      }

      // Sort by timestamp (oldest first)
      items.sort((a, b) => a.timestamp - b.timestamp);

      // Remove items until we have enough space
      let freedSpace = 0;
      let removedCount = 0;

      for (const item of items) {
        if (freedSpace >= requiredSize) break;

        localStorage.removeItem(item.key);
        this.memoryCache.delete(item.key);
        freedSpace += item.size;
        removedCount++;
      }

      if (removedCount > 0) {
        this.currentSize = calculateCurrentSize(this.config.prefix);

        logDebug(
          formatDebugMessage(DEBUG_MESSAGES.REMOVED_OLDEST, {
            count: removedCount,
            size: freedSpace,
          }),
          this.config.debug,
        );
      }

      return freedSpace >= requiredSize;
    } catch (error) {
      logError("SimpleCache: Error removing oldest items", error);
      return false;
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return getDetailedStats({
      currentSize: this.currentSize,
      maxSize: this.config.storage.maxSize,
      utilization: this.currentSize / this.config.storage.maxSize,
      memoryCacheSize: this.memoryCache.size,
      isAvailable: this.isAvailable,
    });
  }

  /**
   * Get cache information for debugging
   */
  getInfo() {
    if (!this.isAvailable) {
      return { available: false, message: ERROR_MESSAGES.STORAGE_UNAVAILABLE };
    }

    const stats = this.getStats();
    const sizeMB = (stats.currentSize / 1024 / 1024).toFixed(2);
    const maxSizeMB = (stats.maxSize / 1024 / 1024).toFixed(2);

    return {
      available: true,
      stats: stats,
      sizeMB: sizeMB,
      maxSizeMB: maxSizeMB,
      config: this.config,
      memoryCacheKeys: Array.from(this.memoryCache.keys()),
    };
  }

  /**
   * Reset statistics
   */
  resetStats() {
    resetStats();
    logDebug(DEBUG_MESSAGES.STATS_RESET, this.config.debug);
  }
}
